import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, ArrowRightStartOnRectangleIcon, BeakerIcon, BuildingOfficeIcon, GlobeAltIcon, EyeIcon, ShieldCheckIcon, EllipsisHorizontalIcon, Cog6ToothIcon, RocketLaunchIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { languages, useLanguage, setLanguage, t, type Language } from '@/app/utils/i18n';
import aqlLogo from 'figma:asset/a561ebfb7d7f12016099e8f63fe09d5462c563b5.png';
import { NotificationCenter } from '@/app/components/notification-center';
import { useAppTheme } from '@/app/providers/theme-provider';
import { useGlobal } from '@/app/providers/global-provider';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

interface CompanyData {
  name: string;
  nif?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
}

interface UserProfile {
  name?: string;
  photo?: string;
  title?: string;
  lastLogin?: Date | string;
  lastLogout?: Date | string;
}

interface UniversalAppHeaderProps {
  appName: string;
  appIcon: React.ReactNode;
  userEmail?: string;
  companyData?: CompanyData;
  userProfile?: UserProfile;
  onBack?: () => void;
  onSignOut?: () => void;
  showSignOut?: boolean;
  className?: string;
  isViewerMode?: boolean; 
  children?: React.ReactNode;
  rightActions?: React.ReactNode;
  compactSignOut?: boolean;
  compactHeader?: boolean;
  contentContainerClassName?: string;
  apiUrl?: string;      // NEW: For notifications
  authToken?: string;   // NEW: For notifications
  notificationUserEmail?: string | null; // NEW: Real account email for push sync
  appId?: string;       // NEW: Explicit ID for notifications
  installAvailable?: boolean; // NEW: PWA Install
  onInstall?: () => void;     // NEW: PWA Install
  showClientBadge?: boolean;
  showBrandBadge?: boolean;
  onOpenSettings?: () => void;
  onOpenAssistant?: () => void;
  assistantActive?: boolean;
  showSystemMenuButton?: boolean;
  visionLabUrl?: string;
}

export function UniversalAppHeader({
  appName,
  appIcon,
  userEmail,
  companyData,
  userProfile,
  onBack,
  onSignOut,
  showSignOut = true,
  className = '',
  isViewerMode = false,
  children,
  rightActions,
  compactSignOut = false,
  compactHeader = false,
  contentContainerClassName = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  apiUrl,
  authToken,
  notificationUserEmail,
  appId,
  installAvailable: propInstallAvailable,
  onInstall: propOnInstall,
  showClientBadge = true,
  showBrandBadge = true,
  onOpenSettings,
  onOpenAssistant,
  assistantActive = false,
  showSystemMenuButton = true,
  visionLabUrl
}: UniversalAppHeaderProps) {
  const language = useLanguage();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showSystemMenu, setShowSystemMenu] = useState(false);
  const [showViewerTooltip, setShowViewerTooltip] = useState(false);
  const [localInstallAvailable, setLocalInstallAvailable] = useState(!!(window as any).deferredPrompt);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { isDark } = useAppTheme();
  const { client, branding } = useGlobal();

  useEffect(() => {
    // Check if already in standalone mode
    if (window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone) {
      setIsStandalone(true);
    }

    // Detect mobile or iOS
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(mobile);

    const handleAvailable = () => setLocalInstallAvailable(true);
    const handleUnavailable = () => setLocalInstallAvailable(false);

    window.addEventListener('aql-install-available', handleAvailable);
    window.addEventListener('aql-install-unavailable', handleUnavailable);

    // Initial check
    if ((window as any).deferredPrompt) setLocalInstallAvailable(true);

    return () => {
      window.removeEventListener('aql-install-available', handleAvailable);
      window.removeEventListener('aql-install-unavailable', handleUnavailable);
    };
  }, []);

  const handleInstall = async () => {
    if (propOnInstall) {
      propOnInstall();
      return;
    }

    const prompt = (window as any).deferredPrompt;
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      console.log(`User response to install: ${outcome}`);
      (window as any).deferredPrompt = null;
      setLocalInstallAvailable(false);
      window.dispatchEvent(new CustomEvent('aql-install-unavailable'));
    } else {
      // Manual trigger for iOS or when event hasn't fired
      window.dispatchEvent(new CustomEvent('aql-show-pwa-prompt'));
    }
  };

  const installVisible = propInstallAvailable !== undefined 
    ? propInstallAvailable 
    : (localInstallAvailable || (isMobile && !isStandalone));

  // Map display name to backend app ID
  const getAppId = (name: string): any => {
    if (appId) return appId;
    const lower = name.toLowerCase();
    if (lower.includes('tasks') || lower.includes('time')) return 'aql-time';
    if (lower.includes('vision')) return 'aql-vision';
    if (lower.includes('meet')) return 'aql-meet';
    if (lower.includes('clock') || lower.includes('relógio') || lower.includes('reloj')) return 'aql-clock';
    if (lower.includes('docs')) return 'aql-docs';
    if (lower.includes('events') || lower.includes('eventos')) return 'aql-events';
    return 'aql-apps';
  };

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setShowLangMenu(false);
  };

  const getAppColor = (name: string): string => {
    const id = getAppId(name);
    switch (id) {
      case 'aql-clock': return 'bg-slate-900';
      case 'aql-vision': return 'bg-orange-500';
      case 'aql-time': return 'bg-blue-600';
      case 'aql-meet': return 'bg-indigo-600';
      case 'aql-docs': return 'bg-blue-700';
      case 'aql-fisio': return 'bg-blue-600';
      case 'aql-barber': return 'bg-slate-900';
      default: return 'bg-blue-600';
    }
  };

  const appColor = getAppColor(appName);
  const isVisionApp = getAppId(appName) === 'aql-vision';
  const headerHeightClass = compactHeader ? 'h-14' : 'h-16';
  const backIconClass = compactHeader ? 'w-4.5 h-4.5' : 'w-5 h-5';
  const leftGapClass = compactHeader ? 'gap-2 md:gap-3' : 'gap-2 md:gap-4';
  const brandGapClass = compactHeader ? 'gap-2' : 'gap-2 md:gap-3';
  const iconWrapClass = compactHeader
    ? 'relative w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg'
    : 'relative w-11 h-11 md:w-12 md:h-12 flex-shrink-0 flex items-center justify-center rounded-xl';
  const iconScaleClass = compactHeader ? 'scale-[1.08]' : 'scale-100';
  const badgeWrapClass = compactHeader
    ? 'absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full shadow-md border border-gray-200 flex items-center justify-center'
    : 'absolute -bottom-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-white rounded-full shadow-md border border-gray-200 flex items-center justify-center';
  const titleClass = compactHeader ? 'text-sm md:text-base' : 'text-base md:text-lg';
  const subtitleClass = compactHeader ? 'text-[10px]' : 'text-[10px] md:text-xs';
  const headerSurfaceClass = isDark
    ? 'bg-[#151f31] text-white border-slate-700/50'
    : 'bg-white/95 text-slate-950 border-slate-200/90 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl';
  const headerTitleClass = isDark ? 'text-white' : 'text-slate-950';
  const headerSubtleTextClass = isDark ? 'text-slate-300' : 'text-slate-600';
  const headerIconButtonClass = isDark
    ? 'text-white hover:bg-white/10'
    : 'text-slate-700 hover:bg-slate-100/85';
  const headerUtilityButtonClass = isDark
    ? 'border-white/10 bg-white/[0.04] text-white hover:bg-white/10'
    : 'border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100';
  const headerUtilityMutedIconClass = isDark ? 'text-white/70' : 'text-slate-500';
  const systemMenuClass = isDark
    ? 'border-sky-300/25 bg-[#0b1628]/98 shadow-[0_40px_110px_rgba(0,0,0,0.74),0_18px_48px_rgba(2,6,23,0.62),0_0_0_1px_rgba(96,165,250,0.14),0_0_36px_rgba(56,189,248,0.08)]'
    : 'border-slate-200 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.18),0_10px_30px_rgba(15,23,42,0.10)]';
  const visionIconSurfaceClass = isDark
    ? 'text-white bg-transparent'
    : 'text-sky-600 bg-transparent';
  const visionIconToneClass = isDark ? 'text-white' : 'text-sky-600';

  return (
    <>
<header className={`sticky top-0 z-50 border-b opacity-100! transition-colors ${headerSurfaceClass} ${className}`}>
        <div className={contentContainerClassName}>
          <div className={`flex items-center justify-between ${headerHeightClass} gap-2`}>
            {/* Left: Back button (if provided) */}
            <div className={`flex items-center ${leftGapClass} min-w-0 flex-1`}>
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl transition-colors ${headerIconButtonClass}`}
                  aria-label="Voltar ao início"
                  title="Voltar ao início"
                >
                  <ArrowLeftIcon className={backIconClass} />
                </button>
              ) : null}

              {/* App Logo + Name with AQL Badge */}
              <div className={`flex items-center ${brandGapClass} min-w-0`}>
                {/* Icon with AQL badge overlay */}
                <div
                  className={`${iconWrapClass} ${
                    isVisionApp && compactHeader && !showBrandBadge
                      ? visionIconSurfaceClass
                      : isVisionApp
                        ? `${visionIconToneClass} bg-transparent shadow-none`
                        : `${appColor} text-white shadow-sm`
                  }`}
                  style={undefined}
                >
                  <div className={`${iconScaleClass} flex items-center justify-center`}>
                    {appIcon}
                  </div>
                  {showBrandBadge ? (
                    <div className={badgeWrapClass}>
                      <img
                        src={isVisionApp && branding.logoUrl ? branding.logoUrl : aqlLogo}
                        alt={isVisionApp && client?.name ? client.name : 'AQL'}
                        className="w-3 h-3 rounded-full object-contain md:h-4 md:w-4"
                        onError={(event) => {
                          if (event.currentTarget.src !== aqlLogo) {
                            event.currentTarget.src = aqlLogo;
                          }
                        }}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <h1 className={`${titleClass} truncate font-bold leading-tight ${headerTitleClass}`}>{appName}</h1>
                    {showClientBadge && isVisionApp && client?.name ? (
                      <span
                        className="hidden rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground/80 sm:inline-flex"
                        style={{ borderColor: `${branding.primaryColor}55`, backgroundColor: `${branding.primaryColor}1A` }}
                      >
                        {client.name}
                      </span>
                    ) : null}
                  </div>
                  
                </div>
              </div>
              
              {/* Presence or other custom content */}
              <div className="hidden min-w-0 flex-1 lg:block">
                {children}
              </div>
            </div>

            {/* Center: Company Info (if provided) - Desktop */}
            {companyData && companyData.name && (
              <div className="hidden lg:flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg border border-emerald-200">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <BuildingOfficeIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-900">{companyData.name}</p>
                  {companyData.nif && (
                    <p className="text-xs text-emerald-700">NIF: {companyData.nif}</p>
                  )}
                </div>
              </div>
            )}

            {/* Right: Priority utilities + compact system menu */}
            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
              {!isViewerMode && apiUrl && authToken && (
                <NotificationCenter 
                  appName={getAppId(appName)}
                  appDisplayName={appName}
                  apiUrl={apiUrl}
                  authToken={authToken}
                  userEmail={notificationUserEmail}
                />
              )}

              {isViewerMode && (
                <div className="relative">
                <button
                  onMouseEnter={() => setShowViewerTooltip(true)}
                  onMouseLeave={() => setShowViewerTooltip(false)}
                  onClick={() => setShowViewerTooltip(!showViewerTooltip)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-sky-500 transition-colors hover:bg-accent md:h-9 md:w-9"
                  aria-label="Viewer mode"
                >
                    <EyeIcon className="w-4 h-4 md:w-5 md:h-5" />
                  </button>

                  {/* Tooltip */}
                  {showViewerTooltip && (
                    <div className="absolute right-0 top-full z-50 mt-2 whitespace-nowrap rounded-lg bg-popover px-3 py-2 text-sm text-popover-foreground shadow-lg">
                      {t('common', 'viewerMode', language)}
                      <div className="absolute -top-1 right-3 h-2 w-2 rotate-45 transform bg-popover"></div>
                    </div>
                  )}
                </div>
              )}

              {showSystemMenuButton ? <Popover open={showSystemMenu} onOpenChange={setShowSystemMenu}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`flex h-9 items-center gap-2 rounded-xl border px-2.5 transition-colors ${headerUtilityButtonClass}`}
                    aria-label="Menu de utilizador e sistema"
                  >
                    <UserCircleIcon className="h-4 w-4" />
                    <EllipsisHorizontalIcon className={`h-4 w-4 ${headerUtilityMutedIconClass}`} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  sideOffset={10}
                  className={`w-64 rounded-2xl p-1.5 backdrop-blur-2xl ${systemMenuClass}`}
                >
               

                  {userProfile && (
                    <>
                      <div className="mx-1.5 my-2 rounded-xl bg-accent/30 p-3">
                        <div className="flex items-start gap-3">
                          {userProfile.photo && (
                            <img
                              src={userProfile.photo}
                              alt={userProfile.name || 'User'}
                              className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                            />
                          )}
                          {!userProfile.photo && (
                            <div className="h-10 w-10 rounded-lg bg-cyan-400/20 flex items-center justify-center flex-shrink-0">
                              <UserCircleIcon className="h-5 w-5 text-cyan-400" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            {userProfile.name && (
                              <p className="text-sm font-semibold text-popover-foreground truncate">{userProfile.name}</p>
                            )}
                            {userProfile.title && (
                              <p className="text-xs text-muted-foreground truncate">{userProfile.title}</p>
                            )}
                            <div className="mt-2 space-y-1">
                              {userProfile.lastLogin && (
                                <p className="text-xs text-muted-foreground">
                                  <span className="font-medium">Entrada:</span> {typeof userProfile.lastLogin === 'string' ? userProfile.lastLogin : new Date(userProfile.lastLogin).toLocaleString('pt-PT', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              )}
                              {userProfile.lastLogout && (
                                <p className="text-xs text-muted-foreground">
                                  <span className="font-medium">Saída:</span> {typeof userProfile.lastLogout === 'string' ? userProfile.lastLogout : new Date(userProfile.lastLogout).toLocaleString('pt-PT', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="-mx-1 my-1 h-px bg-border" />
                    </>
                  )}

                  {onOpenAssistant ? (
                    <button
                      type="button"
                      onClick={() => {
                        onOpenAssistant();
                        setShowSystemMenu(false);
                      }}
                      className="flex w-full items-start gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <RocketLaunchIcon className="mt-0.5 h-4 w-4 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">{assistantActive ? 'Fechar assistente' : 'Assistente'}</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">Ajuda operacional contextual</span>
                      </span>
                    </button>
                  ) : null}

                  {onOpenSettings ? (
                    <button
                      type="button"
                      onClick={() => {
                        onOpenSettings();
                        setShowSystemMenu(false);
                      }}
                      className="hidden w-full items-start gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors hover:bg-accent hover:text-accent-foreground lg:flex"
                    >
                      <Cog6ToothIcon className="mt-0.5 h-4 w-4 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">Definições</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">Configuração de módulos e permissões</span>
                      </span>
                    </button>
                  ) : null}

                  {rightActions ? (
                    <div className="px-1 py-1 [&_button]:w-full [&_button]:justify-start [&_button]:rounded-xl [&_button]:px-2.5 [&_button]:py-2">
                      {rightActions}
                    </div>
                  ) : null}

                  {visionLabUrl ? (
                    <a
                      href={visionLabUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowSystemMenu(false)}
                      className="flex w-full items-start gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <BeakerIcon className="mt-0.5 h-4 w-4 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">AQL Vision Lab</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">Treino, teste e validação de modelos</span>
                      </span>
                    </a>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => {
                      const url = new URL(window.location.href);
                      url.searchParams.set('page', 'privacy');
                      window.location.href = url.toString();
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <ShieldCheckIcon className="h-4 w-4" />
                    <span>{t('common', 'privacy', language)}</span>
                  </button>

                  {showSignOut && onSignOut ? (
                    <>
                      <div className="-mx-1 my-1 h-px bg-border" />
                      <button
                        type="button"
                        onClick={() => {
                          onSignOut();
                          setShowSystemMenu(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
                      >
                        <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                        <span>{t('common', 'signOut', language)}</span>
                      </button>
                    </>
                  ) : null}
                </PopoverContent>
              </Popover> : null}
            </div>
          </div>

          {/* Company Info - Mobile/Tablet */}
          {companyData && companyData.name && (
            <div className="lg:hidden pb-3 pt-1">
              <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg border border-emerald-200">
                <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded flex items-center justify-center">
                  <BuildingOfficeIcon className="w-3 h-3 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-emerald-900 truncate">{companyData.name}</p>
                  {companyData.nif && (
                    <p className="text-xs text-emerald-700">NIF: {companyData.nif}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
